import React, { useEffect, useState } from "react";
import OrderLinesTable from "../../../../components/Orders/OrderLinesTable";
// Type on this page comes from the console scale (--lk-text-* in
// assets/css/lusuku-overrides.css): titles --lk-text-md, body --lk-text,
// button text --lk-text-sm, icon glyphs 16px. The inline 20/19/14px sizes it
// carried sat beside 13px panels and read as a different app (25 Oct 2026).
import {
  Container,
  Card,
  Row,
  Col,
  Button,
  Spinner,
  Alert,
  Form,
  Modal,
  ProgressBar,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { withStyles } from "@material-ui/core/styles";
import { FormControlLabel, Switch, Badge } from "@material-ui/core";
import { useParams, useHistory } from "react-router-dom";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { io } from "socket.io-client";
import {
  MdCancel,
  MdLocalPrintshop,
  MdSportsMotorsports,
  MdArrowBackIosNew,
  MdOutlineCreditCardOff,
  MdOutlineCreditCard,
  MdMap,
  MdLocalShipping,
  MdUndo,
} from "react-icons/md";
import { toast } from "react-toastify";
import axios from "axios";
import {
  getRetailerWholesalerOrderDetails,
  refreshRetailerWholesalerOrderDetails,
  updateRetailerWholesalerOrderStatus,
} from "../../../../actions/retailerWholesalerVendorOrdersActions";
import { markOrderAsPaid } from "../../../../actions/webOrderActions";
import "./RetailerWholesalerOrderDetails.css";
import { getUserFromLocalStorage } from "../../../../utilities/user.js";
import ThreePLPartnerDropdown from "../../../../components/ThreePLPartnerDropdown";
import ContactAction from "../../../../components/Orders/ContactAction";
import DropoffMap from "../../../../components/Maps/DropoffMap";
import { openReceiptForPrint } from "../../../../utilities/openReceipt";
import OrderNextStep from "../../../../components/Orders/OrderNextStep";
import { getShippingAddressText } from "../../../../utilities/orderPresentation";
import socketAuthOptions from "../../../../utilities/socketAuth";
import { attachVendorOrderRooms } from "../../../../hooks/useVendorOrderRooms";
import { vendorOwnerIdOf } from "../../../../utilities/vendorScope";
import useOrderLiveUpdates from "../../../../hooks/useOrderLiveUpdates";
import {
  getDeliveryProgress,
  canVendorDispatch,
  canVendorMarkDelivered,
} from "../../../../utilities/orderDeliverySteps";
import { useGate } from "../../../../components/team/Gated";
import AppSelect from "../../../../components/ui/AppSelect";
import VendorHandoffActions from "../../../../components/Orders/VendorHandoffActions";

const API_URL = process.env.REACT_APP_SERVER_URL;

const IOSSwitch = withStyles((theme) => ({
  root: {
    width: 42,
    height: 26,
    padding: 0,
    margin: theme.spacing(1),
  },
  switchBase: {
    padding: 1,
    "&$checked": {
      transform: "translateX(16px)",
      color: theme.palette.common.white,
      "& + $track": {
        backgroundColor: "#52d869",
        opacity: 1,
        border: "none",
      },
    },
    "&$focusVisible $thumb": {
      color: "#52d869",
      border: "6px solid #fff",
    },
  },
  thumb: {
    width: 24,
    height: 24,
  },
  track: {
    borderRadius: 26 / 2,
    border: `1px solid ${theme.palette.grey[400]}`,
    backgroundColor: theme.palette.grey[50],
    opacity: 1,
    transition: theme.transitions.create(["background-color", "border"]),
  },
  checked: {},
  focusVisible: {},
}))(({ classes, ...props }) => {
  return (
    <Switch
      focusVisibleClassName={classes.focusVisible}
      disableRipple
      classes={{
        root: classes.root,
        switchBase: classes.switchBase,
        thumb: classes.thumb,
        track: classes.track,
        checked: classes.checked,
      }}
      {...props}
    />
  );
});

const isDevelopment = process.env.NODE_ENV === "development";

const RetailerWholesalerOrderDetails = () => {
  const { id } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const user = getUserFromLocalStorage();

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [riders, setRiders] = useState([]);
  const [riderId, setRiderId] = useState("");
  // Vendor team permissions: every status, dispatch, rider and 3PL change
  // on this page is orders:manage (components/team/Gated.js).
  const ordersGate = useGate("orders:manage");
  const [loadingRiders, setLoadingRiders] = useState(true);
  const [elapsedAssignedTime, setElapsedAssignedTime] = useState(0);
  const [elapsedDispatchedTime, setElapsedDispatchedTime] = useState(0);

  const [showMapModal, setShowMapModal] = useState(false);

  // 3PL Partner assignment state
  const [assignmentMode, setAssignmentMode] = useState("rider"); // "rider" or "3pl"
  const [selectedPartnerId, setSelectedPartnerId] = useState("");
  const [assigning3PL, setAssigning3PL] = useState(false);
  const [recalling3PL, setRecalling3PL] = useState(false);
  const [showRecallModal, setShowRecallModal] = useState(false);
  const [autoAssigning, setAutoAssigning] = useState(false);
  const [autoAssignEnabled, setAutoAssignEnabled] = useState(false);

  const orderDetails = useSelector(
    (state) => state.retailerWholesalerVendorOrders
  );
  const { order, loading, error, isUpdateDeliveryStatusComplete } =
    orderDetails;

  const vendorId = user?.result?._id;

  // Live order news (a rider's pickup, a 3PL delivery, a decline) through the
  // one shared feed; this page's order lives in the retailer/wholesaler slice,
  // so it re-reads silently through that slice's own action.
  useOrderLiveUpdates(id, {
    refetch: () => dispatch(refreshRetailerWholesalerOrderDetails(id, vendorId)),
    onChange: (fresh) => {
      if (fresh?.delivery_status === "PROCESSING" && !fresh?.riderId) setRiderId("");
    },
  });

  useEffect(() => {
    dispatch(getRetailerWholesalerOrderDetails(id, vendorId));
    if (isUpdateDeliveryStatusComplete) {
      dispatch(getRetailerWholesalerOrderDetails(id));
    }
  }, [isUpdateDeliveryStatusComplete, id, dispatch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  useEffect(() => {
    const socketUrl = process.env.REACT_APP_SERVER_URL;

    // Authenticated: without a token the server treats this socket as
    // anonymous, and every gated handler (joinVendorRoom, getRiders scoping)
    // silently refuses. See utilities/socketAuth.js.
    //
    // This also passed `{ transports: ["websocket"] }` as a THIRD argument.
    // io() takes two, so that object was dropped on the floor and the page
    // connected over long-polling and upgraded, rather than going straight to
    // a websocket like its sibling pages.
    const socket = io(
      socketUrl,
      socketAuthOptions({ userId: user?.result?._id })
    );

    socket.on("connect", () => {
      if (isDevelopment) {
        console.log("Webclient Connected");
      }
      socket.emit("getRiders");
    });

    // The vendor's order rooms carry the auto-assign feedback: one join that
    // names the owner (4 Oct 2026); the old join is the fallback for an
    // older server.
    const ownerId = vendorOwnerIdOf(user);
    const stopRooms = attachVendorOrderRooms(socket, ownerId, {
      fallback: (s) => s.emit("joinVendorRoom", ownerId),
    });

    socket.on("lusukuRiders", (socketData) => {
      setRiders(socketData);
      setLoadingRiders(false);
    });

    // Auto-assign feedback — only process events for the current order
    socket.on("autoAssign:assigned", (data) => {
      if (data.orderId !== id) return;
      setAutoAssigning(false);
      setAutoAssignEnabled(false);
      const riderName = data.riderName || "a rider";
      toast.success(`Order auto-assigned to ${riderName}`);
      dispatch(getRetailerWholesalerOrderDetails(id, vendorId));
    });

    socket.on("autoAssign:result", (data) => {
      if (data.orderId !== id) return;
      setAutoAssigning(false);
      setAutoAssignEnabled(false);
      if (data.status === "exhausted" || data.status === "no_riders") {
        toast.warn("No available in-house riders found. Please assign manually.");
      }
    });

    return () => {
      stopRooms();
      socket.disconnect();
    };
  }, [user?.result?._id]);

  const handleBack = () => {
    // Navigate back to appropriate orders page based on user role
    const isSuperAdmin = user?.result?.user_role === "SUPERUSER" || user?.result?.userType === "LUSUKU STAFF";

    if (isSuperAdmin) {
      history.push("/admin/paginatedOrdersTable");
    } else {
      history.push("/admin/retailerWholesalerOrdersTable");
    }
  };

  const handleUpdateStatus = (newStatus) => {
    if (newStatus === "ASSIGNED" && !riderId) {
      toast.warn("Please select a rider before assigning the order.");
      return;
    }
    if (newStatus === "ASSIGNED") {
      assignRetailerWholesalerOrderToRider();
    } else {
      dispatch(updateRetailerWholesalerOrderStatus(order?._id, newStatus));
    }
  };

  const handleCancelOrder = () => {
    dispatch(updateRetailerWholesalerOrderStatus(order?._id, "REJECTED"));
    setShowCancelModal(false);
  };

  // Print Receipt opens the receipt document (the one with the fiscal
  // markings when issued), not a print of this page.
  const handlePrint = () => openReceiptForPrint(order?._id);

  const handleMarkAsPaid = () => {
    if (user?.result?.userType !== "LUSUKU STAFF" && user?.result?.user_role !== "SUPERUSER") {
      toast.warn("🚫 You are not authorized to mark order as paid!");
      return;
    } else {
      dispatch(markOrderAsPaid(order?._id));
    }
  };

  // Trigger vendor auto-assignment using in-house riders
  const handleVendorAutoAssign = async () => {
    if (!vendorId) {
      toast.warn("Vendor ID not found");
      setAutoAssignEnabled(false);
      return;
    }
    setAutoAssigning(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}/weborders/${order?._id}/vendor-auto-assign`,
        { vendorId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast.success("Auto-assigning to nearest available rider…");
        setTimeout(() => dispatch(getRetailerWholesalerOrderDetails(order?._id, vendorId)), 4000);
      } else {
        toast.error(response.data.error?.message || "Auto-assign failed");
        setAutoAssignEnabled(false);
      }
    } catch (error) {
      toast.error(
        error.response?.data?.error?.message || "Failed to trigger auto-assign"
      );
      setAutoAssignEnabled(false);
    } finally {
      setAutoAssigning(false);
    }
  };

  const assignRetailerWholesalerOrderToRider = () => {
    const delivery_status = "ASSIGNED";
    const assignedTime = Date.now();
    toast.success(`Assigning order ${order?._id} to rider ${riderId}`);
    localStorage.setItem(`order_${order?._id}_assignedTime`, assignedTime);
    dispatch(updateRetailerWholesalerOrderStatus(order?._id, delivery_status));
    setTimeout(() => {
      dispatch(getRetailerWholesalerOrderDetails(order?._id));
    }, 500);
  };

  // Assign order to 3PL Partner
  const assignOrderTo3PLPartner = async () => {
    if (!selectedPartnerId) {
      toast.warn("Please select a 3PL partner");
      return;
    }

    setAssigning3PL(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}/weborders/${order?._id}/assign-to-3pl`,
        { partnerId: selectedPartnerId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        toast.success(response.data.message || "Order assigned to 3PL partner!");
        // Refresh order details
        setTimeout(() => {
          dispatch(getRetailerWholesalerOrderDetails(order?._id, vendorId));
        }, 500);
      } else {
        toast.error(response.data.error?.message || "Failed to assign order");
      }
    } catch (error) {
      console.error("Error assigning to 3PL:", error);
      toast.error(
        error.response?.data?.error?.message || "Failed to assign order to 3PL partner"
      );
    } finally {
      setAssigning3PL(false);
    }
  };

  // Recall order from 3PL Partner
  const recallOrderFrom3PL = async () => {
    setShowRecallModal(false);
    setRecalling3PL(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}/weborders/${order?._id}/recall-from-3pl`,
        { reason: "Recalled by vendor" },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(response.data.message || "Order recalled successfully!");
        dispatch(getRetailerWholesalerOrderDetails(order?._id, vendorId));
      } else {
        toast.error(response.data.error?.message || "Failed to recall order");
      }
    } catch (error) {
      console.error("Error recalling from 3PL:", error);
      toast.error(
        error.response?.data?.error?.message || "Failed to recall order from 3PL partner"
      );
    } finally {
      setRecalling3PL(false);
    }
  };


  const handleDispatchRetailerWholesalerOrder = () => {
    const delivery_status = "DISPATCHED";
    const dispatchedTime = Date.now();
    localStorage.setItem(`order_${order?._id}_dispatchedTime`, dispatchedTime);
    dispatch(updateRetailerWholesalerOrderStatus(order?._id, delivery_status));
    setTimeout(() => {
      dispatch(getRetailerWholesalerOrderDetails(order?._id));
    }, 500);
  };

  const handleDeliverRetailerWholesalerOrder = () => {
    const delivery_status = "DELIVERED";
    dispatch(updateRetailerWholesalerOrderStatus(order?._id, delivery_status));
    setTimeout(() => {
      dispatch(getRetailerWholesalerOrderDetails(order?._id));
    }, 500);
  };

  // The delivery ladder lives in utilities/orderDeliverySteps.js so this page,
  // its sibling vendor pages and the next-step banner can never disagree about
  // where an order is. This was a four-case switch whose `default: 0` emptied
  // the bar for the six statuses this page never writes itself: ACCEPTED and
  // PICKED_UP come from the rider app (OrderDetailsScreen / DispatchOrderScreen),
  // PENDING_3PL / IN_TRANSIT / ARRIVED from the 3PL rail, FULFILLED from the
  // counter. A rider tapping Accept therefore made the vendor's screen look
  // like the order had gone backwards to zero.
  //
  // NOTE: there is no "PAID" delivery_status. Payment state lives in
  // `isPaid`/`paymentStatus` (see utilities/paymentStatus.js); the
  // delivery_status enum in models/Orders.js has no such member.
  const getProgress = () => getDeliveryProgress(order);

  const getBadgeStyle = (status) => {
    switch (status) {
      case "PROCESSING":
        return {
          backgroundColor: "orange",
          color: "white",
          borderRadius: "10px",
          padding: "5px",
        };
      case "ASSIGNED":
        return {
          backgroundColor: "blue",
          color: "white",
          borderRadius: "10px",
          padding: "5px",
        };
      case "DISPATCHED":
        return {
          backgroundColor: "purple",
          color: "white",
          borderRadius: "10px",
          padding: "5px",
        };
      case "DELIVERED":
        return {
          backgroundColor: "green",
          color: "white",
          borderRadius: "10px",
          padding: "5px",
        };
      case "REJECTED":
        return {
          backgroundColor: "red",
          color: "white",
          borderRadius: "10px",
          padding: "5px",
        };
      default:
        return {
          backgroundColor: "grey",
          color: "white",
          borderRadius: "10px",
          padding: "5px",
        };
    }
  };

  const getPaidBadgeStyle = (isPaid) => {
    return {
      backgroundColor: isPaid ? "green" : "#2C3A40",
      color: "white",
      borderRadius: "10px",
      padding: "3px",
    };
  };

  // Only an order still in PROCESSING can be cancelled here. This used to also
  // accept a "PAID" delivery_status, which is not a member of the delivery_status
  // enum and could never be stored — dead code that made the rule look broader
  // than it is. Behaviour is unchanged.
  const canCancelOrder = order?.delivery_status === "PROCESSING";

  const getDisabledStatusOptions = () => {
    const disabledOptions = {
      PROCESSING: order?.delivery_status !== "PROCESSING",
      ASSIGNED:
        order?.delivery_status !== "PROCESSING" &&
        !canVendorDispatch(order),
      DISPATCHED:
        !canVendorDispatch(order) &&
        !canVendorMarkDelivered(order),
      DELIVERED:
        !canVendorMarkDelivered(order) &&
        order?.delivery_status !== "DELIVERED",
      REJECTED:
        order?.delivery_status === "DELIVERED" ||
        order?.delivery_status === "REJECTED",
    };
    return disabledOptions;
  };

  const disabledStatusOptions = getDisabledStatusOptions();

  useEffect(() => {
    if (order?.delivery_status === "ASSIGNED") {
      const storedAssignedTime = localStorage.getItem(
        `order_${order?._id}_assignedTime`
      );
      if (storedAssignedTime) {
        const elapsed = Math.floor(
          (Date.now() - parseInt(storedAssignedTime, 10)) / 1000
        );
        setElapsedAssignedTime(elapsed);
      }
    }
    if (order?.delivery_status === "DISPATCHED") {
      const storedDispatchedTime = localStorage.getItem(
        `order_${order?._id}_dispatchedTime`
      );
      if (storedDispatchedTime) {
        const elapsed = Math.floor(
          (Date.now() - parseInt(storedDispatchedTime, 10)) / 1000
        );
        setElapsedDispatchedTime(elapsed);
      }
    }
    const interval = setInterval(() => {
      if (order?.delivery_status === "ASSIGNED") {
        setElapsedAssignedTime((prev) => prev + 1);
      }
      if (order?.delivery_status === "DISPATCHED") {
        setElapsedDispatchedTime((prev) => prev + 1);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [order?.delivery_status, order?._id]);

  if (loading) {
    return (
      <Spinner
        animation="grow"
        variant="primary"
        style={{
          marginTop: "70px",
          top: "50%",
          left: "50%",
          position: "fixed",
        }}
      />
    );
  }

  if (error) {
    return (
      <Alert variant="danger" style={{ marginTop: "70px" }}>
        {error}
      </Alert>
    );
  }

  const handleShowMap = () => {
    setShowMapModal(true);
  };

  const handleCloseMap = () => {
    setShowMapModal(false);
  };

  return (
    <Container fluid>
      <Row className="mb-2">
        <Col md="12">
          <Button variant="secondary" onClick={handleBack}>
            <MdArrowBackIosNew
              style={{
                marginRight: "3px",
                fontSize: "16px",
                color: "#ffff",
                fontWeight: "bold",
              }}
            />
            Back to Orders
          </Button>
        </Col>
      </Row>
      <Row>
        <Col md="12">
          <Card className="shadow-sm">
            <Card.Header
              className="justify-content-between align-items-center rod-card-header-inner"
              style={{
                backgroundColor: "#012030",
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <div>
                <Card.Title as="h4" style={{ color: "white", marginBottom: "4px" }}>
                  Order Details - Order #{order?._id}
                </Card.Title>
                {order?.createdAt && (
                  <small style={{ color: "#a8c6df", display: "block" }}>
                    {moment(order.createdAt).format("MMM D, YYYY · h:mm a")}
                  </small>
                )}
              </div>
              {order.isMultiVendorOrder && (
                <Alert variant="success" className="mt-3 text-dark">
                  This is a multi-vendor order. You are fulfilling{" "}
                  {order.vendorItemCount} out of {order.totalItemCount} items.
                  Please process your items and send them 📦📦 to the hub for
                  combined delivery
                  <MdSportsMotorsports
                    style={{
                      marginLeft: "5px",
                      fontSize: "16px",
                      color: "red",
                    }}
                  />
                </Alert>
              )}

              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <VendorHandoffActions orderId={order?._id} />
                <Button
                  className="d-none d-md-block rod-print-btn"
                  style={{
                    backgroundColor: "#13678A",
                    color: "white",
                    border: "none",
                    fontSize: "var(--lk-text-sm)",
                    padding: "10px",
                  }}
                  onClick={handlePrint}
                >
                  Print Receipt
                  <MdLocalPrintshop style={{ marginLeft: "4px" }} />
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md="6" className="rod-left-col">
                  <h5
                    className="rod-section-title"
                    style={{
                      color: "#023059",
                      fontSize: "var(--lk-text-md)",
                      fontWeight: 600,
                    }}
                  >
                    Order Items
                  </h5>
                  <div className="table-responsive">
                    <OrderLinesTable order={order} />
                  </div>
                </Col>
                <Col md="6" className="rod-right-col">
                  <h5
                    className="rod-section-title"
                    style={{
                      color: "#023059",
                      fontSize: "var(--lk-text-md)",
                      fontWeight: 600,
                    }}
                  >
                    Order Summary
                  </h5>
                  <Card
                    className="mb-3 p-3 shadow"
                    style={{ boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.2)" }}
                  >
                    <p style={{ fontSize: "var(--lk-text)" }}>
                      <strong
                        style={{
                          color: "#012030",
                          fontSize: "var(--lk-text)",
                        }}
                      >
                        Customer Name:
                      </strong>
                      { } {order?.customerName}
                    </p>
                    <p style={{ fontSize: "var(--lk-text)" }}>
                      <strong
                        style={{
                          color: "#012030",
                          fontSize: "var(--lk-text)",
                        }}
                      >
                        Payment Method:
                      </strong>
                      { } {order?.paymentMethod}
                    </p>
                    <p style={{ fontSize: "var(--lk-text)" }}>
                      <strong
                        style={{
                          color: "#012030",
                          fontSize: "var(--lk-text)",
                        }}
                      >
                        Items Price:
                      </strong>
                      { } Ush {order?.itemsPrice?.toLocaleString()}
                    </p>
                    <p style={{ fontSize: "var(--lk-text)" }}>
                      <strong
                        style={{
                          color: "#012030",
                          fontSize: "var(--lk-text)",
                        }}
                      >
                        Tax Price:
                      </strong>
                      { } Ush {order?.taxPrice?.toLocaleString()}
                    </p>
                    <p style={{ fontSize: "var(--lk-text)" }}>
                      <strong
                        style={{
                          color: "#012030",
                          fontSize: "var(--lk-text)",
                        }}
                      >
                        Total Price:
                      </strong>
                      { } Ush {order?.totalPrice?.toLocaleString()}
                    </p>
                    <p style={{ fontSize: "var(--lk-text)" }}>
                      <strong
                        style={{
                          color: "#012030",
                          fontSize: "var(--lk-text)",
                        }}
                      >
                        Payment Status:
                      </strong>{" "}
                      <Badge
                        overlap="rectangular"
                        style={getPaidBadgeStyle(order?.isPaid)}
                      >
                        {order?.isPaid ? (
                          <MdOutlineCreditCard
                            style={{
                              fontSize: "var(--lk-text)",
                              verticalAlign: "middle",
                            }}
                          />
                        ) : (
                          <MdOutlineCreditCardOff
                            style={{
                              fontSize: "var(--lk-text)",
                              verticalAlign: "middle",
                            }}
                          />
                        )}
                      </Badge>
                    </p>
                    <p style={{ fontSize: "var(--lk-text)" }}>
                      <strong
                        style={{
                          color: "#012030",
                          fontSize: "var(--lk-text)",
                        }}
                      >
                        Order Status:
                      </strong>{" "}
                      <Badge
                        overlap="rectangular"
                        style={getBadgeStyle(order?.delivery_status)}
                      >
                        {order.delivery_status}
                      </Badge>
                    </p>
                    <p style={{ fontSize: "var(--lk-text)" }}>
                      <strong
                        style={{
                          color: "#012030",
                          fontSize: "var(--lk-text)",
                        }}
                      >
                        Order Date:
                      </strong>
                      { }{" "}
                      {moment(order?.createdAt).format(
                        "MMMM Do YYYY, h:mm:ss a"
                      )}
                    </p>
                  </Card>
                  <h5
                    style={{
                      color: "#023059",
                      fontSize: "var(--lk-text-md)",
                      fontWeight: 600,
                    }}
                  >
                    Shipping Address
                  </h5>
                  <Card
                    className="p-3 shadow"
                    style={{ boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)" }}
                  >
                    <p style={{ fontSize: "var(--lk-text)" }}>
                      {getShippingAddressText(order?.shippingAddress, "No address recorded")}
                    </p>
                    <ContactAction
                      label="Customer"
                      name={order?.customerName}
                      phone={order?.shippingAddress?.customerNumber || order?.shippingAddress?.recipientPhone}
                      compact
                      style={{ marginBottom: 10 }}
                    />

                    {/* The same drop-off block as the admin order page:
                        place name, precision chip, Show map, Send pin,
                        Copy pin. This page printed the raw coordinates
                        instead, which nobody can read. */}
                    {Number.isFinite(Number(order?.shippingAddress?.latLng?.lat)) &&
                      Number.isFinite(Number(order?.shippingAddress?.latLng?.lng)) && (
                        <DropoffMap
                          lat={order.shippingAddress.latLng.lat}
                          lng={order.shippingAddress.latLng.lng}
                          address={getShippingAddressText(order?.shippingAddress, "")}
                          shippingAddress={order?.shippingAddress}
                          orderRef={order?.order_number || order?._id}
                          rider={order?.riderId}
                          country={order?.country}
                        />
                      )}
                    <p style={{ fontSize: "var(--lk-text)", marginTop: 8 }}>
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id="map-tooltip">
                            View Delivery Address on Map!
                          </Tooltip>
                        }
                      >
                        <Button
                          variant="link"
                          onClick={handleShowMap}
                          style={{
                            padding: 0,
                            marginLeft: "10px",
                            size: "30px",
                          }}
                        >
                          <MdMap size={30} color="#063" />
                        </Button>
                      </OverlayTrigger>
                    </p>
                  </Card>
                  {/* Map Modal */}
                  <Modal show={showMapModal} onHide={handleCloseMap} size="lg">
                    <Modal.Header closeButton>
                      <Modal.Title>
                        Delivery Location Map ||{" "}
                        {getShippingAddressText(order?.shippingAddress)}
                      </Modal.Title>
                      {/* Divider line */}
                      <hr
                        style={{
                          borderTop: "1px solid #063",
                          width: "100%",
                          marginTop: "5px",
                        }}
                      />
                    </Modal.Header>
                    <Modal.Body>
                      <iframe
                        width="100%"
                        height="450"
                        frameBorder="0"
                        style={{ border: 0 }}
                        src={`https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&q=${order?.shippingAddress?.latLng?.lat},${order?.shippingAddress?.latLng?.lng}&zoom=15`}
                        allowFullScreen
                      ></iframe>
                    </Modal.Body>
                    <Modal.Footer>
                      <Button variant="primary" onClick={handleCloseMap}>
                        Close
                      </Button>
                    </Modal.Footer>
                  </Modal>
                </Col>
              </Row>
              <Row className="mt-4">
                <Col
                  md="6"
                  className="d-flex flex-column justify-content-between"
                >
                  <Form.Group
                    controlId="formAssignment"
                    className="shadow p-3 h-100"
                    style={{
                      boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.2)",
                      height: "100%",
                    }}
                  >
                    {/* Assignment Mode Toggle */}
                    {!order?.riderId && order?.delivery_status === "PROCESSING" && (
                      <div className="mb-3">
                        <Form.Label>Assignment Type</Form.Label>
                        <div className="d-flex gap-2 align-items-center flex-wrap">
                          <Button
                            variant={assignmentMode === "rider" ? "primary" : "outline-primary"}
                            size="sm"
                            onClick={() => setAssignmentMode("rider")}
                          >
                            <MdSportsMotorsports style={{ marginRight: "5px" }} />
                            Assign to Rider
                          </Button>
                          <Button
                            variant={assignmentMode === "3pl" ? "success" : "outline-success"}
                            size="sm"
                            onClick={() => setAssignmentMode("3pl")}
                          >
                            <MdLocalShipping style={{ marginRight: "5px" }} />
                            Assign to 3PL Partner
                          </Button>
                          <FormControlLabel
                            style={{ margin: 0 }}
                            control={
                              <Switch
                                checked={autoAssignEnabled}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  setAutoAssignEnabled(checked);
                                  if (checked) handleVendorAutoAssign();
                                }}
                                disabled={(autoAssigning) || !ordersGate.allowed}
                                title={ordersGate.title}
                                color="default"
                                size="small"
                              />
                            }
                            label={
                              <span style={{ fontSize: "var(--lk-text)", fontWeight: 500, color: "#b45309" }}>
                                {autoAssigning ? (
                                  <><Spinner size="sm" animation="border" className="me-1" style={{ width: "12px", height: "12px" }} />Finding rider…</>
                                ) : (
                                  <>⚡ Auto-Assign My Riders</>
                                )}
                              </span>
                            }
                          />
                        </div>
                      </div>
                    )}

                    {/* Show 3PL Partner Assignment if in PENDING_3PL status */}
                    {order?.delivery_status === "PENDING_3PL" && (
                      <Alert style={{
                        backgroundColor: "rgba(11, 41, 71, 1)",
                        borderRadius: "12px",
                        padding: "16px 20px"
                      }} className="mb-3">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <div className="d-flex align-items-center mb-2">
                              <MdLocalShipping style={{ marginRight: "8px", fontSize: "20px" }} />
                              <strong style={{ fontSize: "var(--lk-text-md)" }}>Pending 3PL Assignment</strong>
                            </div>
                            <div style={{ opacity: 0.9, fontSize: "var(--lk-text)", lineHeight: 1.5 }}>
                              Order sent to: <strong>{order?.assignmentDetails?.partnerId?.companyName || "Unknown"}</strong>
                              <br />
                              Waiting for 3PL to assign a rider.
                            </div>
                          </div>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            style={{
                              borderRadius: "8px",
                              padding: "8px 16px",
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              fontWeight: "500",
                              borderWidth: "2px",
                              backgroundColor: "rgba(220, 38, 38, 0.1)",
                              borderColor: "#dc2626",
                              color: "#fff"
                            }}
                            onClick={() => setShowRecallModal(true)}
                            disabled={(recalling3PL) || !ordersGate.allowed}
                            title={ordersGate.title}
                          >
                            {recalling3PL ? (
                              <>
                                <Spinner animation="border" size="sm" />
                                Recalling...
                              </>
                            ) : (
                              <>
                                <MdUndo style={{ fontSize: "16px" }} />
                                Recall Order
                              </>
                            )}
                          </Button>
                        </div>
                      </Alert>
                    )}

                    {/* Rider Selection (default or when mode is 'rider') */}
                    {assignmentMode === "rider" && (
                      <>
                        <Form.Label>Select Rider</Form.Label>
                        <AppSelect block
                          value={order?.riderId?._id || riderId}
                          onChange={(e) => setRiderId(e.target.value)}
                          disabled={
                            !!order?.riderId ||
                            order?.delivery_status === "DELIVERED" ||
                            order?.delivery_status === "PENDING_3PL"
                          }
                        >
                          <option value="">Select Rider</option>
                          {loadingRiders ? (
                            <option value="" disabled>
                              Searching for riders...
                            </option>
                          ) : (
                            riders?.map((rider) => (
                              <option key={rider._id} value={rider._id}>
                                {rider.firstName + " " + rider.lastName} ||{" "}
                                {rider.onlineStatus}
                                {rider.codCash?.isFlagged ? " ⚠️ COD LIMIT" : ""}
                              </option>
                            ))
                          )}
                        </AppSelect>
                        {loadingRiders && (
                          <div style={{ marginLeft: "2px" }}>
                            <Spinner animation="border" size="sm" />
                          </div>
                        )}
                      </>
                    )}

                    {/* 3PL Partner Selection */}
                    {assignmentMode === "3pl" && order?.delivery_status === "PROCESSING" && (
                      <ThreePLPartnerDropdown
                        dropoff={order?.shippingAddress?.latLng}
                        selectedPartnerId={selectedPartnerId}
                        onSelectPartner={setSelectedPartnerId}
                        disabled={order?.delivery_status !== "PROCESSING"}
                      />
                    )}

                    {/* Show assigned rider info */}
                    {order?.riderId && (
                      <p
                        style={{
                          marginTop: "10px",
                          fontSize: "var(--lk-text-sm)",
                          fontStyle: "italic",
                        }}
                      >
                        Assigned to:{" "}
                        {order?.riderId?.firstName +
                          " " +
                          order?.riderId?.lastName}
                        <MdSportsMotorsports
                          style={{
                            marginLeft: "5px",
                            fontSize: "16px",
                            color: "green",
                          }}
                        />
                        <br />
                        {order?.delivery_status === "ASSIGNED" && (
                          <span>
                            Time Elapsed after Assigning:{" "}
                            {Math.floor(elapsedAssignedTime / 3600)}h{" "}
                            {Math.floor((elapsedAssignedTime % 3600) / 60)}m{" "}
                            {elapsedAssignedTime % 60}s
                          </span>
                        )}
                        {order?.delivery_status === "DISPATCHED" && (
                          <span>
                            Time Elapsed from Dispatch:{" "}
                            {Math.floor(elapsedDispatchedTime / 3600)}h{" "}
                            {Math.floor((elapsedDispatchedTime % 3600) / 60)}m{" "}
                            {elapsedDispatchedTime % 60}s
                          </span>
                        )}
                      </p>
                    )}

                    {/* Assignment Buttons */}
                    {!order?.riderId && order?.delivery_status === "PROCESSING" && (
                      <>
                        {assignmentMode === "rider" && riderId && (
                          <Button
                            variant="primary"
                            onClick={assignRetailerWholesalerOrderToRider}
                            className="mt-2"
                            disabled={(!riderId || loadingRiders) || !ordersGate.allowed}
                            title={ordersGate.title}
                            style={{ backgroundColor: "#8C1F28", color: "white" }}
                          >
                            Assign Order to Rider
                          </Button>
                        )}
                        {assignmentMode === "3pl" && selectedPartnerId && (
                          <Button
                            variant="success"
                            onClick={assignOrderTo3PLPartner}
                            className="mt-2"
                            disabled={(!selectedPartnerId || assigning3PL) || !ordersGate.allowed}
                            title={ordersGate.title}
                          >
                            {assigning3PL ? (
                              <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Assigning...
                              </>
                            ) : (
                              <>
                                <MdLocalShipping style={{ marginRight: "5px" }} />
                                Send to 3PL Partner
                              </>
                            )}
                          </Button>
                        )}
                      </>
                    )}
                  </Form.Group>
                </Col>
                <Col md="6">
                  <Form.Group
                    controlId="formOrderStatus"
                    className="shadow p-3 h-100"
                    style={{ boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.2)" }}
                  >
                    <Form.Label>Update Order Status</Form.Label>
                    <AppSelect block
                      disabled={!ordersGate.allowed}
                      title={ordersGate.title}
                      value={order.delivery_status}
                      onChange={(e) => handleUpdateStatus(e.target.value)}
                    >
                      <option
                        value="PROCESSING"
                        disabled={disabledStatusOptions.PROCESSING}
                      >
                        1. Processing
                      </option>
                      <option
                        value="ASSIGNED"
                        disabled={disabledStatusOptions.ASSIGNED}
                      >
                        2. Assigned
                      </option>
                      <option
                        value="DISPATCHED"
                        disabled={disabledStatusOptions.DISPATCHED}
                      >
                        3. Dispatched
                      </option>
                      <option
                        value="DELIVERED"
                        disabled={disabledStatusOptions.DELIVERED}
                      >
                        4. Delivered
                      </option>
                      <option
                        value="REJECTED"
                        disabled={disabledStatusOptions.REJECTED}
                      >
                        5. Cancel Order
                      </option>
                    </AppSelect>
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mt-4">
                <Col className="d-flex justify-content-end align-items-center rod-action-row">
                  <div
                    style={{
                      cursor: !canCancelOrder ? "not-allowed" : "pointer",
                      backgroundColor: !canCancelOrder
                        ? "#8C1F28"
                        : "transparent",
                      borderRadius: "5px",
                      padding: "2px",
                      marginRight: "5px",
                      width: "160px",
                      height: "40px",
                    }}
                  >
                    <Button
                      variant="danger"
                      onClick={() => setShowCancelModal(true)}
                      className="mr-2"
                      disabled={(!canCancelOrder) || !ordersGate.allowed}
                      title={ordersGate.title}
                      style={{
                        backgroundColor: !canCancelOrder
                          ? "transparent"
                          : "red",
                        color: !canCancelOrder ? "white" : "white",
                        border: "none",
                        fontSize: "var(--lk-text-sm)",
                        width: "100%",
                        height: "100%",
                        padding: "6px",
                      }}
                    >
                      <MdCancel
                        style={{
                          fontSize: "16px",
                          justifyContent: "center",
                          alignItems: "center",
                          marginRight: "5px",
                        }}
                      />
                      Cancel Order
                    </Button>
                  </div>
                  {/* components/Orders/ContactAction: a real tel: link on a
                      phone, copy on a desktop. The old button only raised a
                      toast with the number. */}
                  <ContactAction
                    label="Customer"
                    name={order?.customerName}
                    phone={order?.shippingAddress?.customerNumber || order?.shippingAddress?.recipientPhone}
                    style={{ marginRight: 5 }}
                  />
                </Col>
              </Row>
              <Row className="mt-4">
                <Col md="12">
                  {/* What to do next, and who the order is waiting on. Shared
                      with the restaurant, healthcare and brand pages. */}
                  <OrderNextStep order={order} style={{ marginBottom: "12px" }} />
                  <ProgressBar
                    now={getProgress()}
                    label={`${getProgress()}%`}
                    style={{ height: "30px" }}
                    variant={
                      order?.delivery_status === "DELIVERED"
                        ? "success"
                        : undefined
                    }
                  />
                </Col>
              </Row>
              {/* Lower Dispatch and Mark as delivered Buttons */}
              <Row className="mt-4 d-none d-md-flex">
                <Col md="12" className="d-flex justify-content-between rod-dispatch-row">
                  <div
                    style={{
                      cursor:
                        !canVendorDispatch(order)
                          ? "not-allowed"
                          : "pointer",
                      backgroundColor:
                        !canVendorDispatch(order)
                          ? "#4B4952"
                          : "transparent",
                      borderRadius: "5px",
                      padding: "2px",
                    }}
                  >
                    <Button
                      variant="warning"
                      onClick={handleDispatchRetailerWholesalerOrder}
                      disabled={(!canVendorDispatch(order)) || !ordersGate.allowed}
                      title={ordersGate.title}
                      style={{
                        backgroundColor:
                          !canVendorDispatch(order)
                            ? "transparent"
                            : "orange",
                        color:
                          !canVendorDispatch(order)
                            ? "white"
                            : "black",
                        border: "none",
                        fontSize: "var(--lk-text-sm)",
                      }}
                    >
                      Dispatch Order
                    </Button>
                  </div>
                  <div
                    style={{
                      cursor:
                        !canVendorMarkDelivered(order)
                          ? "not-allowed"
                          : "pointer",
                      backgroundColor:
                        !canVendorMarkDelivered(order)
                          ? "green"
                          : "transparent",
                      borderRadius: "5px",
                      padding: "2px",
                    }}
                  >
                    <Button
                      variant="success"
                      onClick={handleDeliverRetailerWholesalerOrder}
                      disabled={(!canVendorMarkDelivered(order)) || !ordersGate.allowed}
                      title={ordersGate.title}
                      style={{
                        backgroundColor:
                          !canVendorMarkDelivered(order)
                            ? "transparent"
                            : "green",
                        color:
                          !canVendorMarkDelivered(order)
                            ? "white"
                            : "black",
                        border: "none",
                        fontSize: "var(--lk-text-sm)",
                      }}
                    >
                      Mark as Delivered
                    </Button>
                  </div>
                  {/* Mark As Paid Button */}
                  <div
                    style={{
                      cursor: order?.isPaid ? "not-allowed" : "pointer",
                    }}
                  >
                    <FormControlLabel
                      control={
                        <IOSSwitch
                          checked={order?.isPaid}
                          onChange={handleMarkAsPaid}
                          color="primary"
                          disabled={(order?.delivery_status !== "DELIVERED" ||
                            order?.isPaid) || !ordersGate.allowed}
                          title={ordersGate.title}
                        />
                      }
                      label={order?.isPaid ? "Paid" : "Mark as Paid"}
                    />
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Cancellation</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to cancel this order?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCancelModal(false)}>
            Close
          </Button>
          <Button variant="danger" onClick={handleCancelOrder}>
            Cancel Order
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Recall Order Confirmation Modal */}
      <Modal show={showRecallModal} onHide={() => setShowRecallModal(false)} centered>
        <Modal.Header closeButton style={{ borderBottom: "none", paddingBottom: 0 }}>
          <Modal.Title style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <MdUndo style={{ color: "#dc2626", fontSize: "24px" }} />
            Recall Order
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ paddingTop: "12px" }}>
          <p style={{ marginBottom: "12px" }}>
            Are you sure you want to recall this order from{" "}
            <strong>{order?.assignmentDetails?.partnerId?.companyName || "the 3PL partner"}</strong>?
          </p>
          <Alert style={{ fontSize: "var(--lk-text)", marginBottom: 0, backgroundColor: "rgba(5, 17, 68, 1)" }}>
            <strong>Note:</strong> This will remove the order from the 3PL partner's queue and return it to your processing list.
          </Alert>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: "none", paddingTop: 0 }}>
          <Button
            variant="outline-secondary"
            onClick={() => setShowRecallModal(false)}
            style={{ borderRadius: "8px" }}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={recallOrderFrom3PL}
            disabled={recalling3PL}
            style={{
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              gap: "6px"
            }}
          >
            {recalling3PL ? (
              <>
                <Spinner animation="border" size="sm" />
                Recalling...
              </>
            ) : (
              <>
                <MdUndo />
                Confirm Recall
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default RetailerWholesalerOrderDetails;
